// src/components/forms/admin/CompanyInfoForm.jsx
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import api from "./../Interceptor";
import { CustomInput } from "./ui";
import { getRoute } from "../api/routes";
import ModalFooter from "./customModal/modalFooter";
import {
  validateFormHandler,
  validateLiveField,
} from "../utils/validations/validateForm";

const CompanyInfo = () => {
  const companyId = localStorage.getItem("companyId");

  const [name, setName] = useState(localStorage.getItem("companyName") || "");
  const [about, setAbout] = useState(
    localStorage.getItem("companyAbout") || "",
  );

  // 🖼️ Company Logo
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(
    localStorage.getItem("companyLogo") || "",
  );

  // ✍️ Signature Image
  const [selectedSignature, setSelectedSignature] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState(
    localStorage.getItem("companySignature") || "",
  );

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showFooterShadow, setShowFooterShadow] = useState(false);
  const contentRef = useRef(null);

  const handleImageChange = (event, type) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === "logo") {
          setSelectedImage(file);
          setImagePreview(reader.result);
        } else {
          setSelectedSignature(file);
          setSignaturePreview(reader.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async (image) => {
    const data = new FormData();
    data.append("file", image);
    data.append("upload_preset", "ExpenseTracker");
    try {
      const response = await fetch(
        "https://api.cloudinary.com/v1_1/dqvgbgje8/image/upload",
        { method: "POST", body: data },
      );
      const urlData = await response.json();
      return urlData.url;
    } catch (error) {
      toast.error("Image upload failed.");
      return null;
    }
  };
  const handleNameChange = (e) => {
    const value = e.target.value;
    setName(value);

    const error = validateLiveField("name", value);

    setErrors((prev) => ({ ...prev, name: error || undefined }));
  };
  const handleSave = async () => {
    const validationErrors = validateFormHandler({ name, about });
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setIsLoading(true);

      // Upload images if new ones selected
      let imageUrl = imagePreview;
      let signatureUrl = signaturePreview;

      if (selectedImage) imageUrl = await uploadImage(selectedImage);
      if (selectedSignature)
        signatureUrl = await uploadImage(selectedSignature);

      const response = await api.put(
        getRoute("users", "admin", "companyInfo", false),
        {
          id: companyId,
          name,
          about,
          logo: imageUrl,
          signature: signatureUrl, // 🆕 Added signature field
        },
      );

      if (response.status !== 200)
        throw new Error("Failed to save company info");

      const data = response.data;

      // Save locally
      localStorage.setItem("companyName", data.name);
      localStorage.setItem("companyLogo", data.logo);
      localStorage.setItem("companyAbout", data.about);
      if (data.signature)
        localStorage.setItem("companySignature", data.signature);

      toast.success("Company Info saved successfully");
      // onClose?.();
    } catch (error) {
      toast.error("Error saving company info");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const checkScrollable = () => {
      setShowFooterShadow(el.scrollHeight > el.clientHeight);
    };

    checkScrollable();

    window.addEventListener("resize", checkScrollable);
    return () => window.removeEventListener("resize", checkScrollable);
  }, []);
  return (
    <div className="rounded-3xl bg-white border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 p-4 mb-4 flex flex-col gap-6 font-customFont">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">
            {name ? "Update" : "Add"} Company Information
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Manage your company branding
          </p>
        </div>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSave();
        }}
        className="space-y-6"
      >
        <div ref={contentRef} className="space-y-6">
          {/* Company Name */}
          <div className="bg-gray-50/60 rounded-2xl p-4">
            <CustomInput
              type="text"
              label="Company Name"
              id="name"
              value={name}
              onChange={handleNameChange}
              message={errors.name}
              requir="true"
            />
          </div>

          {/* About */}
          <div className="bg-gray-50/60 rounded-2xl p-4">
            <CustomInput
              label="About Company"
              as="textarea"
              id="about"
              value={about}
              onChange={(e) => {
                setAbout(e.target.value);
                if (errors.about)
                  setErrors((prev) => ({ ...prev, about: undefined }));
              }}
              message={errors.about}
              requir="true"
            />
          </div>

          {/* Branding Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Company Logo */}
            <div className="bg-gray-50/60 rounded-2xl p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Logo
              </label>

              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <input
                    type="file"
                    id="logo"
                    accept="image/*"
                    onChange={(e) => handleImageChange(e, "logo")}
                    className="w-full text-sm file:mr-4 file:py-2.5 file:px-4
                    file:rounded-lg file:border-0
                    file:bg-themeGradient file:text-white
                    hover:file:opacity-90
                    border border-gray-200 rounded-xl"
                  />
                </div>

                {imagePreview && (
                  <div className="w-20 h-20 rounded-xl border bg-white flex items-center justify-center shadow-sm">
                    <img
                      src={imagePreview}
                      alt="Company Logo Preview"
                      className="max-w-full max-h-full object-contain p-1"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Signature Image */}
            <div className="bg-gray-50/60 rounded-2xl p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Signature Image
              </label>

              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <input
                    type="file"
                    id="signature"
                    accept="image/*"
                    onChange={(e) => handleImageChange(e, "signature")}
                    className="w-full text-sm file:mr-4 file:py-2.5 file:px-4
                    file:rounded-lg file:border-0
                    file:bg-themeGradient file:text-white
                    hover:file:opacity-90
                    border border-gray-200 rounded-xl"
                  />
                </div>

                {signaturePreview && (
                  <div className="w-28 h-16 rounded-xl border bg-white flex items-center justify-center shadow-sm">
                    <img
                      src={signaturePreview}
                      alt="Signature Preview"
                      className="max-w-full max-h-full object-contain p-1"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex justify-end pt-4 border-t">
          <button
            type="submit"
            disabled={isLoading}
            className="
            px-8 py-2.5 rounded-xl
            bg-themeGradient text-white font-semibold
            shadow-sm hover:shadow-md
            hover:opacity-95
            active:scale-[0.98]
            transition-all duration-200
            disabled:opacity-60 disabled:cursor-not-allowed
          "
          >
            {name
              ? isLoading
                ? "Updating..."
                : "Update Company"
              : isLoading
                ? "Adding..."
                : "Add Company"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CompanyInfo;
